/**
 * Phase 14.1 — canonical carrier directory.
 *
 * Source of truth for /carriers, /carriers/[slug], CarrierStrip, and the
 * AvailableFromStrip on product pages. `productLines` values must match the
 * real product slugs returned by GET /api/products — this is what powers the
 * cross-navigation between carrier -> product and product -> carrier.
 *
 * Compliance rule: this file contains NO carrier marketing claims. Ratings
 * are always hedged ("Rated A- or better where indicated").
 */
export const CARRIERS = [
  {
    slug: "allianz",
    name: "Allianz",
    legalName: "Allianz Life Insurance Company of North America",
    website: "https://www.allianzlife.com",
    overview:
      "Allianz is a global insurance and asset management group with a U.S. life insurance subsidiary headquartered in Minneapolis. Known in the U.S. market for fixed index annuities and indexed universal life product lines.",
    rating: "Rated A- or better where indicated",
    productLines: ["iul-accumulation", "iul-protection", "annuity-accumulation"],
  },
  {
    slug: "american-amicable",
    name: "American Amicable",
    legalName: "American Amicable Life Insurance Company of Texas",
    website: "https://www.americanamicable.com",
    overview:
      "Texas-based life insurer offering simplified-issue and final-expense life insurance products often used in middle-market and senior protection planning.",
    rating: "Rated A- or better where indicated",
    productLines: ["term-life", "whole-life", "final-expense"],
  },
  {
    slug: "american-equity",
    name: "American Equity",
    legalName: "American Equity Investment Life Insurance Company",
    website: "https://www.american-equity.com",
    overview:
      "Iowa-based carrier specializing in fixed and indexed annuity products for retirement income planning.",
    rating: "Rated A- or better where indicated",
    productLines: ["annuity-accumulation", "annuity-income"],
  },
  {
    slug: "americo",
    name: "Americo",
    legalName: "Americo Financial Life and Annuity Insurance Company",
    website: "https://americo.com",
    overview:
      "Kansas City-based family-owned carrier offering a broad set of individual life and annuity products, with a focus on mortgage protection and final-expense markets.",
    rating: "Rated A- or better where indicated",
    productLines: ["term-life", "mortgage-protection", "final-expense", "annuity-accumulation", "medicare"],
  },
  {
    slug: "ameritas",
    name: "Ameritas",
    legalName: "Ameritas Life Insurance Corp.",
    website: "https://www.ameritas.com",
    overview:
      "Nebraska-based mutual holding company offering individual and group life, disability income, dental, and annuity products.",
    rating: "Rated A or better where indicated",
    productLines: ["whole-life", "iul-accumulation", "iul-protection", "disability-income", "annuity-accumulation"],
  },
  {
    slug: "annexus",
    name: "Annexus",
    legalName: "Annexus",
    website: "https://www.annexus.com",
    overview:
      "Product design firm partnering with insurance carriers to develop retirement income and accumulation solutions. Annexus-designed products are issued through partner carriers.",
    rating: "Products issued by partner carriers; see partner-carrier rating",
    productLines: ["annuity-accumulation", "iul-accumulation"],
  },
  {
    slug: "athene",
    name: "Athene",
    legalName: "Athene Annuity and Life Company",
    website: "https://www.athene.com",
    overview:
      "Retirement services company specializing in fixed and indexed annuities, headquartered in Iowa. Part of the Apollo Global Management group.",
    rating: "Rated A or better where indicated",
    productLines: ["annuity-accumulation", "annuity-income"],
  },
  {
    slug: "corebridge-financial",
    name: "Corebridge Financial",
    legalName: "Corebridge Financial, Inc.",
    website: "https://www.corebridgefinancial.com",
    overview:
      "One of the largest U.S. providers of retirement solutions and insurance products. Former AIG Life & Retirement business, now operating as an independent public company.",
    rating: "Rated A or better where indicated",
    productLines: ["term-life", "iul-accumulation", "iul-protection", "annuity-accumulation", "annuity-income"],
  },
  {
    slug: "ethos",
    name: "Ethos",
    legalName: "Ethos Technologies Inc. (producer); policies issued by partner carriers",
    website: "https://www.ethoslife.com",
    overview:
      "Technology-forward insurance producer offering term life insurance through streamlined underwriting. Policies are issued by partner insurance carriers.",
    rating: "Partner-carrier backed; ratings vary",
    productLines: ["term-life"],
  },
  {
    slug: "fg",
    name: "F&G",
    legalName: "Fidelity & Guaranty Life Insurance Company",
    website: "https://www.fglife.com",
    overview:
      "Iowa-based provider of fixed and indexed annuities and indexed universal life, part of the Fidelity National Financial group.",
    rating: "Rated A- or better where indicated",
    productLines: ["annuity-accumulation", "annuity-income", "iul-accumulation", "iul-protection"],
  },
  {
    slug: "lincoln-financial",
    name: "Lincoln Financial",
    legalName: "The Lincoln National Life Insurance Company",
    website: "https://www.lincolnfinancial.com",
    overview:
      "Multi-line insurer providing life insurance, annuities, group protection, and retirement plan services. Headquartered in Radnor, PA.",
    rating: "Rated A or better where indicated",
    productLines: ["term-life", "iul-accumulation", "iul-protection", "whole-life", "annuity-accumulation", "annuity-income", "disability-income"],
  },
  {
    slug: "mutual-of-omaha",
    name: "Mutual of Omaha",
    legalName: "Mutual of Omaha Insurance Company",
    website: "https://www.mutualofomaha.com",
    overview:
      "Omaha-based mutual insurer offering life, long-term care, disability income, Medicare supplement, and annuity product lines across the U.S.",
    rating: "Rated A+ or better where indicated",
    productLines: ["term-life", "whole-life", "ltc", "medicare", "disability-income", "annuity-income", "final-expense"],
  },
  {
    slug: "nationwide",
    name: "Nationwide",
    legalName: "Nationwide Life Insurance Company",
    website: "https://www.nationwide.com",
    overview:
      "Columbus, OH-based mutual insurance and financial services company offering life, annuities, retirement plans, and property-casualty. Member of the Fortune 100.",
    rating: "Rated A+ or better where indicated",
    productLines: ["term-life", "whole-life", "iul-accumulation", "iul-protection", "annuity-accumulation", "annuity-income", "ltc"],
  },
  {
    slug: "north-american",
    name: "North American",
    legalName: "North American Company for Life and Health Insurance",
    website: "https://www.northamericancompany.com",
    overview:
      "Life and annuity carrier headquartered in West Des Moines, IA, part of the Sammons Financial Group. Product line emphasis on indexed life and fixed indexed annuity.",
    rating: "Rated A+ or better where indicated",
    productLines: ["iul-accumulation", "iul-protection", "annuity-accumulation", "term-life"],
  },
];

export const CARRIERS_BY_SLUG = Object.fromEntries(CARRIERS.map((c) => [c.slug, c]));

/** Return carriers whose productLines include the given product slug. */
export const carriersForProduct = (productSlug) =>
  CARRIERS.filter((c) => c.productLines.includes(productSlug));

export default CARRIERS;
